'use client';

import { useEffect, useRef, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { useSession } from 'next-auth/react';
import posthog from 'posthog-js';
import { PostHogProvider as AnalyticsProvider } from 'posthog-js/react';
import { getPostHogConfig } from '@/lib/analytics/config';

function synchronizeIdentity(sub: string | undefined) {
  const previousUser = posthog.get_property('$user_id');
  // Reset persisted identity on logout and direct account switches, including reloads.
  if (previousUser && previousUser !== sub) posthog.reset(true);
  if (sub && posthog.get_distinct_id() !== sub) posthog.identify(sub);
}

export function PostHogProvider({ children }: { children: ReactNode }) {
  const { data: session, status } = useSession();
  const sub = session?.user?.sub;
  const pathname = usePathname();
  const lastPageview = useRef<string | null>(null);

  useEffect(() => {
    if (!getPostHogConfig() || status === 'loading') return;
    synchronizeIdentity(status === 'authenticated' ? sub : undefined);
    posthog.set_config({ autocapture: { dom_event_allowlist: ['click'] } });
    const pageviewKey = `${sub || 'anonymous'}:${pathname}`;
    if (pathname && lastPageview.current !== pageviewKey) {
      posthog.capture('$pageview');
      lastPageview.current = pageviewKey;
    }
  }, [status, sub, pathname]);

  return <AnalyticsProvider client={posthog}>{children}</AnalyticsProvider>;
}
